import React, { useState, useEffect } from 'react';
import Papa from 'papaparse'; // npm install papaparse
import Button from '../common/Button';
import apiService from '../../services/api';

const TransactionList = ({ walletId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalPages: 0,
    totalTransactions: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'DESC',
  });

  // Validate walletId before loading transactions
  const loadTransactions = async (
    page = 1,
    limit = 10,
    sortKey = sortConfig.key,
    sortDirection = sortConfig.direction
  ) => {
    if (!walletId) {
      setError('Wallet ID is required.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const skip = (page - 1) * limit;
      const response = await apiService.getTransactions(
        walletId,
        skip,
        limit,
        sortKey,
        sortDirection
      );

      const { transactions: newTransactions, total } = response;

      setTransactions(newTransactions);
      setPagination({
        currentPage: page,
        limit,
        totalPages: Math.ceil(total / limit),
        totalTransactions: total,
      });
    } catch (err) {
      setError(err.message || 'Failed to load transactions.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    if (!key) return;
    const direction =
      sortConfig.key === key && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';

    setSortConfig({ key, direction });
    loadTransactions(1, pagination.limit, key, direction);
  };

  const handlePageChange = (page) => {
    if (
      typeof page !== 'number' ||
      page < 1 ||
      page > pagination.totalPages ||
      loading
    ) {
      return;
    }
    loadTransactions(page, pagination.limit);
  };

  // Export the current transactions to a CSV file and trigger download
  const exportToCSV = () => {
    if (!transactions.length) {
      alert('No transactions to export.');
      return;
    }
    const headers = ['Date', 'Type', 'Amount', 'Balance', 'Description'];
    const csvContent = [
      headers.join(','), // Header row
      ...transactions.map((t) =>
        [
          new Date(t.date).toISOString(), // Format date as ISO string
          t.type,
          t.amount,
          t.balance,
          `"${t.description}"`, // Quote description to handle commas
        ].join(',')
      ),
    ].join('\n'); // Join all rows with newline

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wallet_transactions_${
      new Date().toISOString().split('T')[0]
    }.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Import transactions from CSV
  const handleImportCSV = (event) => {
    const file = event.target.files[0];
    if (!file) {
      alert('Please select a CSV file.');
      return;
    }
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      alert('Only CSV files are supported.');
      return;
    }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        // Validate parsed data
        if (!Array.isArray(results.data) || !results.data.length) {
          alert('No valid transactions found in the CSV.');
          return;
        }
        // Check required fields
        const requiredFields = [
          'Date',
          'Type',
          'Amount',
          'Balance',
          'Description',
        ];
        const missingFields = requiredFields.filter(
          (field) => !(field in results.data[0])
        );
        if (missingFields.length) {
          alert(`Missing required fields in CSV: ${missingFields.join(', ')}`);
          return;
        }
        // Validate each transaction
        for (const t of results.data) {
          if (
            !t.Date ||
            !t.Type ||
            isNaN(parseFloat(t.Amount)) ||
            isNaN(parseFloat(t.Balance))
          ) {
            alert('Invalid transaction data found. Please check your CSV.');
            return;
          }
        }
        try {
          await apiService.importTransactions(walletId, results.data);
          loadTransactions(
            1,
            pagination.limit,
            sortConfig.key,
            sortConfig.direction
          );
          alert('Transactions imported successfully!');
        } catch (err) {
          alert('Import failed: ' + (err.response?.data?.error || err.message));
        }
      },
      error: (err) => {
        alert('CSV parsing error: ' + err.message);
      },
    });
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount) => {
    if (isNaN(amount)) return '0.0000';
    return parseFloat(amount).toFixed(4);
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const { currentPage, totalPages } = pagination;
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++)
          pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  useEffect(() => {
    if (walletId) {
      loadTransactions(1, 10);
    }
  }, [walletId]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">Error loading transactions: {error}</p>
        <Button
          onClick={() => loadTransactions(1, 10)}
          variant="danger"
          className="mt-2"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col h-[80vh] min-h-[500px]">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Transaction History
            </h2>
            {pagination.totalTransactions > 0 && (
              <p className="text-sm text-gray-600 mt-1">
                Showing {(pagination.currentPage - 1) * pagination.limit + 1} to{' '}
                {Math.min(
                  pagination.currentPage * pagination.limit,
                  pagination.totalTransactions
                )}{' '}
                of {pagination.totalTransactions} transactions
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="ml-2 cursor-pointer">
              <span className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-semibold shadow">
                Export CSV
              </span>
              <button
                type="button"
                onClick={exportToCSV}
                className="hidden"
                tabIndex={-1}
                aria-hidden="true"
              />
            </label>
            <label className="ml-2 cursor-pointer">
              <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                Import CSV
              </span>
              <input
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Main content area: scrollable, grows to fill space */}
      <div className="flex-1 overflow-y-auto">
        {transactions.length === 0 && !loading ? (
          <div className="p-8 text-center text-gray-500">
            No transactions found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('date')}
                      className="flex items-center hover:text-gray-700 group"
                    >
                      Date
                      <span className="ml-1 flex flex-col">
                        {sortConfig.key === 'date' ? (
                          sortConfig.direction === 'ASC' ? (
                            '↑'
                          ) : (
                            '↓'
                          )
                        ) : (
                          <span className="opacity-40 group-hover:opacity-60">
                            <span className="block leading-none">↑↓</span>
                          </span>
                        )}
                      </span>
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('amount')}
                      className="flex items-center hover:text-gray-700 group"
                    >
                      Amount
                      <span className="ml-1 flex flex-col">
                        {sortConfig.key === 'amount' ? (
                          sortConfig.direction === 'ASC' ? (
                            '↑'
                          ) : (
                            '↓'
                          )
                        ) : (
                          <span className="opacity-40 group-hover:opacity-60">
                            <span className="block leading-none">↑↓</span>
                          </span>
                        )}
                      </span>
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      Loading transactions...
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(transaction.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            transaction.type === 'CREDIT'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {transaction.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <span
                          className={
                            transaction.amount >= 0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }
                        >
                          {transaction.amount >= 0 ? '+' : ''}₹
                          {formatAmount(transaction.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ₹{formatAmount(transaction.balance)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {transaction.description}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls: always at the bottom */}
      {pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
          <div className="flex-1 flex justify-between sm:hidden">
            {/* Mobile pagination */}
            <Button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1 || loading}
              variant="secondary"
            >
              Previous
            </Button>
            <Button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={
                pagination.currentPage === pagination.totalPages || loading
              }
              variant="secondary"
            >
              Next
            </Button>
          </div>

          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Page{' '}
                <span className="font-medium">{pagination.currentPage}</span> of{' '}
                <span className="font-medium">{pagination.totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                {/* Previous button */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1 || loading}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>←
                </button>

                {/* Page numbers */}
                {generatePageNumbers().map((pageNum, index) =>
                  pageNum === '...' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNum === pagination.currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {pageNum}
                    </button>
                  )
                )}

                {/* Next button */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={
                    pagination.currentPage === pagination.totalPages || loading
                  }
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>→
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionList;
